/**
 * Settings Page
 * Main settings page with tabbed sections for Integrations, Profile, and Workspace
 */

import React, { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import {
  Link as LinkIcon,
  User,
  Building2,
  Loader2,
  AlertCircle,
  Database,
  Bell,
  RefreshCw,
  ShieldCheck,
  History,
  Target,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { YDMPageHero } from "@/components/brand";
import { IntegrationsPanel } from "@/components/settings/IntegrationsPanel";
import { ConnectPlatformModal } from "@/components/settings/ConnectPlatformModal";
import { EditAccountModal } from "@/components/settings/EditAccountModal";
import { ProfilePanel } from "@/components/settings/ProfilePanel";
import { NotificationsPanel } from "@/components/settings/NotificationsPanel";
import { formatLastSync } from "@/components/settings/formatLastSync";
import BackfillPanel from "@/components/settings/BackfillPanel";
import OutcomeImportPanel from "@/components/settings/OutcomeImportPanel";
import { InviteMember } from "@/components/workspace/InviteMember";
import { AVAILABLE_PLATFORMS } from "@/components/settings/availablePlatforms";
import { usePlatformAccounts } from "@/hooks/usePlatformAccounts";
import { usePendingTargetCount } from "@/hooks/usePendingTargetCount";
import { TargetsPanel } from "@/components/settings/targets/TargetsPanel";

type TabId = "integrations" | "data" | "profile" | "workspace" | "notifications" | "targets";

interface Tab {
  id: TabId;
  icon: React.ComponentType<{ className?: string }>;
}

// Available platform configurations

const TABS: Tab[] = [
  { id: "integrations", icon: LinkIcon },
  // Importing a file is connecting a data source, which is what this section
  // of Settings is for — not an action that belongs beside the chart that
  // displays the result.
  { id: "data", icon: Database },
  { id: "profile", icon: User },
  // Sits beside Profile because it is the same kind of setting: yours, not the
  // workspace's. Two people in one workspace can want completely different mail.
  { id: "notifications", icon: Bell },
  { id: "workspace", icon: Building2 },
  // DEC5: every role sees it; what each can do is decided inside the panel.
  { id: "targets", icon: Target },
];

export const SettingsPage: React.FC = () => {
  const { tab = "integrations" } = useParams<{ tab?: TabId }>();
  const navigate = useNavigate();
  const { workspace } = useWorkspace();
  const { t } = useTranslation();
  // Same count as the nav badge (master is synthesised as admin).
  const pendingTargets = usePendingTargetCount(workspace?.id ?? null, workspace?.userRole === "admin");

  const [activeTab, setActiveTab] = useState<TabId>(tab as TabId || "integrations");

  const {
    accountHealth,
    accounts,
    allQueued,
    anySyncable,
    anyTestable,
    backfillNotice,
    canWriteData,
    connectedCount,
    editingAccount,
    emptySyncReasons,
    error,
    frontiers,
    handleBackfill,
    handleConnect,
    handleDisconnect,
    handleEdit,
    handleModalClose,
    handleModalConnect,
    handleReconnect,
    handleSaveEdit,
    handleSync,
    handleSyncAll,
    handleTest,
    handleTestAll,
    historyDays,
    isConnecting,
    isLoading,
    isModalOpen,
    isSyncingAny,
    isTestingAny,
    latestSyncAt,
    pendingConnect,
    realAccounts,
    setBackfillNotice,
    setEditingAccount,
    setError,
    syncingAccounts,
    testingAccounts,
    todayIso,
    unreadableFrontiers,
  } = usePlatformAccounts();

  // Sync active tab with URL. Stays HERE, with the tab state it writes — it was
  // the one effect in the moved block that had nothing to do with accounts.
  useEffect(() => {
    if (tab && TABS.some((candidate) => candidate.id === tab)) {
      setActiveTab(tab as TabId);
    }
  }, [tab]);

  // Update URL when tab changes
  const handleTabChange = useCallback(
    (newTab: TabId) => {
      setActiveTab(newTab);
      navigate(`/settings/${newTab}`, { replace: true });
    },
    [navigate]
  );


  return (
    <div>
      <YDMPageHero
        eyebrow={t("settings.settingsEyebrow")}
        headline={t("settings.title")}
        subtitle={t("settings.subtitle", { name: workspace?.name ?? "…" })}
        actions={
          <div className="flex flex-col items-end gap-2">
            {!isLoading && realAccounts.length > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-ink-950/90 px-3 py-1 text-xs text-white">
                <span
                  className={clsx(
                    "w-1.5 h-1.5 rounded-full",
                    connectedCount === realAccounts.length ? "bg-green-400" : "bg-amber-400"
                  )}
                  aria-hidden="true"
                />
                {t("settings.chipConnected", {
                  connected: connectedCount,
                  total: realAccounts.length,
                })}
                {latestSyncAt && <> · {t("settings.chipLastSync", { ago: formatLastSync(latestSyncAt, t) })}</>}
              </span>
            )}
            <div className="flex items-center gap-2">
              {anyTestable && (
                <button
                  type="button"
                  onClick={handleTestAll}
                  disabled={isTestingAny}
                  className="inline-flex items-center gap-1.5 rounded-md border-2 border-ink-950 bg-white px-3 py-1.5 text-xs font-semibold text-ink-900 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isTestingAny ? (
                    <>
                      <Loader2 size={13} className="animate-spin" aria-hidden="true" />
                      {t("settings.testingAll")}
                    </>
                  ) : (
                    <>
                      <ShieldCheck size={13} aria-hidden="true" />
                      {t('settings.testAll')}
                    </>
                  )}
                </button>
              )}
              {anySyncable && (
                <button
                  type="button"
                  onClick={handleSyncAll}
                  disabled={isSyncingAny}
                  className="inline-flex items-center gap-1.5 rounded-md border-2 border-ink-950 bg-ink-950 px-3 py-1.5 text-xs font-semibold text-white hover:bg-ink-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSyncingAny ? (
                    <>
                      <Loader2 size={13} className="animate-spin" aria-hidden="true" />
                      {allQueued ? t('settings.queued') : t('settings.syncing')}
                    </>
                  ) : (
                    <>
                      <RefreshCw size={13} aria-hidden="true" />
                      {t('settings.syncAll')}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        }
      />

      {/* Tabs — pill nav. Selected pill carries ink-on-white contrast; never
          primary-600 text on the near-white page (~1.28:1, invisible). */}
      <nav className="flex flex-wrap gap-2 mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={clsx(
              "flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors border",
              activeTab === tab.id
                ? "bg-ink-950 border-ink-950 text-white"
                : "bg-white border-neutral-200 text-neutral-700 hover:text-ink-900 hover:border-neutral-300"
            )}
          >
            <tab.icon className="w-4 h-4" />
            <span>{t(`settings.tabs.${tab.id}`)}</span>
            {tab.id === "targets" && pendingTargets !== null && pendingTargets > 0 && (
              <span className="px-2 py-0.5 text-xs bg-primary-100 text-ink-900 rounded-full">{pendingTargets}</span>
            )}
          </button>
        ))}
      </nav>

      {/* Error Banner */}
      {error && (
        <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-lg flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-error-800">{t('settings.error')}</p>
            <p className="text-sm text-error-700 mt-0.5">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            // 2.77:1 on the error-50 banner it sits on — the control that
            // dismisses an error message was the least legible thing in it.
            className="text-error-700 hover:text-error-900"
          >
            ×
          </button>
        </div>
      )}

      {/* Tab Content — no forced min-height: the layout's flex column already
          pins the footer, so padding a short tab only buys a needless scroll */}
      <div>
        {activeTab === "integrations" && (
          <div>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-ink-900 animate-spin" />
              </div>
            ) : (
              <IntegrationsPanel
                platforms={AVAILABLE_PLATFORMS}
                accounts={accounts}
                frontiers={frontiers}
                unreadableFrontiers={unreadableFrontiers}
                todayIso={todayIso}
                syncingAccountIds={syncingAccounts}
                health={accountHealth}
                testingAccountIds={testingAccounts}
                emptySyncReasons={emptySyncReasons}
                onConnect={handleConnect}
                onSync={handleSync}
                onReconnect={handleReconnect}
                onDisconnect={handleDisconnect}
                onTest={handleTest}
                onEdit={handleEdit}
              />
            )}

            {backfillNotice && (
              <div
                role="status"
                className="mb-4 flex items-start gap-2 rounded-lg border border-neutral-300 bg-neutral-50 p-3 text-sm text-ink-800"
              >
                <History className="mt-0.5 h-4 w-4 shrink-0 text-ink-900" aria-hidden="true" />
                <span className="flex-1">{backfillNotice}</span>
                <button
                  type="button"
                  onClick={() => setBackfillNotice(null)}
                  aria-label={t("backfill.dismiss")}
                  className="text-neutral-700 hover:text-neutral-700"
                >
                  ×
                </button>
              </div>
            )}

            {/* Below the account list, because it acts on the accounts above and
                is a once-per-account operation rather than part of the routine
                flow. No demo guard needed: realAccounts already excludes demo
                rows, and the panel renders nothing for an empty list. */}
            <div className="mt-6">
              <BackfillPanel
                accounts={realAccounts.map((a) => ({
                  id: a.id,
                  platform: a.platform,
                  accountName: a.accountName,
                }))}
                canEdit={canWriteData}
                onRequest={handleBackfill}
              />
            </div>
          </div>
        )}

        {activeTab === "data" && (
          workspace ? (
            /* FRAMED BY WHAT THE FILE DOES TO THE MODEL: the outcome series is
               what a marketing-mix model EXPLAINS.

               The sales-agent importer that sat below it is hidden since
               2026-10-20 (owner), the way the hidden pages were: only the
               Dashboard and /campaigns read media_brokers, and both are hidden.
               BrokerImportPanel, its service, its tables and its keys are kept
               for when they come back. Why the two importers were never one
               form (a row per day vs a row per person) is in 4c6bc0d^.

               The workspace is named here, once, rather than by the panel: a
               platform master can write into any workspace. */
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-bold text-ink-900">
                  {t('settings.dataFrameTitle')}
                </h2>
                <p className="mt-0.5 text-sm text-neutral-700">
                  {t('settings.dataFrameIntro', { workspace: workspace.name })}
                </p>
              </div>

              <section aria-labelledby="data-role-outcome">
                <h3 id="data-role-outcome" className="text-sm font-bold text-ink-900">
                  {t('settings.dataRoleOutcome')}
                </h3>
                <p className="mt-0.5 mb-2 text-xs leading-relaxed text-neutral-700">
                  {t('settings.dataRoleOutcomeHint')}
                </p>
                <OutcomeImportPanel workspaceId={workspace.id} canEdit={canWriteData} />
              </section>
            </div>
          ) : (
            <p className="text-sm text-neutral-700">{t('misc.loadingWorkspace')}</p>
          )
        )}

        {activeTab === "profile" && <ProfilePanel />}

      {activeTab === "notifications" && <NotificationsPanel />}

        {activeTab === "targets" && (
          workspace
            ? <TargetsPanel key={workspace.id} workspaceId={workspace.id} />
            : <p className="text-sm text-neutral-700">{t('misc.loadingWorkspace')}</p>
        )}

        {activeTab === "workspace" && (
          // ข้อเท็จจริงที่ระบบมีอยู่แล้ว แทนกล่อง "จะมาในอนาคต" — หน้าไม่หลอก
          // ให้กดแท็บเปล่า การแก้ไขยังไม่เปิดใช้ และป้ายบอกตรง ๆ
          <div className="bg-white rounded-xl border border-neutral-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-neutral-700" aria-hidden="true" />
              <h2 className="text-sm font-semibold text-ink-900">{t('settings.workspaceHeading')}</h2>
              <span className="px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700 text-[11px]">
                {t("settings.workspaceReadOnly")}
              </span>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-lg border border-neutral-100 bg-neutral-50/50 px-4 py-3">
                <dt className="text-xs text-neutral-700">{t("settings.workspaceName")}</dt>
                <dd className="mt-1 text-lg font-semibold text-ink-900">
                  {workspace?.name ?? "—"}
                </dd>
              </div>
              <div className="rounded-lg border border-neutral-100 bg-neutral-50/50 px-4 py-3">
                <dt className="text-xs text-neutral-700">{t("settings.workspaceAccounts")}</dt>
                <dd className="mt-1 text-lg font-semibold text-ink-900">
                  {isLoading
                    ? "…"
                    : t("settings.workspaceAccountsValue", { count: realAccounts.length })}
                </dd>
              </div>
              <div className="rounded-lg border border-neutral-100 bg-neutral-50/50 px-4 py-3">
                <dt className="text-xs text-neutral-700">{t("settings.workspaceHistory")}</dt>
                <dd className="mt-1 text-lg font-semibold text-ink-900">
                  {historyDays !== null
                    ? t("settings.workspaceHistoryValue", { count: historyDays })
                    : "—"}
                </dd>
              </div>
            </dl>
            <p className="mt-4 text-xs text-neutral-700">{t("settings.workspaceEditNote")}</p>
          </div>
        )}

        {/* Inviting someone is a workspace action, and this is the workspace
            tab — but until now the component existed with no route to it at
            all: invitations could be ACCEPTED at /invite/accept and never
            sent. A finished feature nobody can reach is the same as an absent
            one. */}
        {activeTab === "workspace" && workspace?.id && (
          <div className="mt-6">
            <InviteMember workspaceId={workspace.id} workspaceName={workspace.name} />
          </div>
        )}
      </div>

      {/* Connect Platform Modal */}
      <ConnectPlatformModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        platform={pendingConnect?.platform ?? null}
        onConnect={handleModalConnect}
        isConnecting={isConnecting}
        workspaceId={pendingConnect?.workspaceId}
        workspaceName={pendingConnect?.workspaceName}
      />

      <EditAccountModal
        account={editingAccount}
        isOpen={editingAccount !== null}
        onClose={() => setEditingAccount(null)}
        onSave={handleSaveEdit}
      />
    </div>
  );
};

export default SettingsPage;
